import { deleteRevenue } from "src/app/core/v2/state/valuation-process/_actions";
import { CostAssessmentRecordRow, DepreciatedRecordRow, DepreciatedValueRow } from "../../types";
import { RecordOptions } from "./calculation";
import { buildCodeTree, normalizeRecords } from "./normalize";
import * as moment from "moment";
import { ModelAssumptionsUtil } from "src/app/core/v2/state/valuation-process/core/income/model-assumptions-util";

export type MixedOptions = {
  kind: 'mixed',
  from: string
  levelId: number
}

type WithShow<T> = T & {
  show_depreciation: boolean,
  is_depreciation_complete: boolean
}

export function mixedMethod(
  records: CostAssessmentRecordRow[],
  recordOptions: RecordOptions,
  options: MixedOptions
): {
  records: DepreciatedRecordRow[],
  graphValues: {age: number, depreciatedValue: number, depreciationFactor?: number}[],
  totalCost: number,
  totalDepreciatedCost: number,
  yearDiff: number
} {
  const { levelId, from } = options
  const { size, valuationDate, buildingConstructionDate } = recordOptions
  const _normalizedRecords = normalizeRecords(records, recordOptions)
  const _groupedRecords = groupRecords(_normalizedRecords, levelId)

  const codeToDepCost = new Map<string, number>()
  const showCodes = new Set<string>()
  const completeByCode = new Map<string, boolean>()
  for (const g of _groupedRecords) {
    const depreciation = chooseGroupDepreciation(g.key, _normalizedRecords)
    if (g.key) {
      showCodes.add(g.key)
      completeByCode.set(g.key, isDepreciationComplete(depreciation))
    }

    if (depreciation !== undefined) {
      for (const row of g.items) {
        const value = mixedCalc(row, {valuationDate, constructionDate: buildingConstructionDate}, depreciation)
        codeToDepCost.set(row.code, value ?? 0)
      }
    }
  }
  const merged: WithShow<DepreciatedRecordRow>[] = _normalizedRecords.map(r => {
    const base: WithShow<DepreciatedRecordRow> = {...r, show_depreciation: false, is_depreciation_complete: false}
    if (r.kind === 'value') {
      const val = codeToDepCost.get(r.code)
      if (val !== null) {
        base.depreciated_cost = val
        base.depreciated_per_unit = val / size
      } else {
        base.depreciated_cost = 0
        base.depreciated_per_unit = 0
      }
    }

    if (showCodes.has(r.code)) {
      base.show_depreciation = true
      const c = completeByCode.get(r.code)
      if (typeof c === 'boolean') {
        base.is_depreciation_complete = c
      }
    }
    return base
  })
  const values = merged.filter(r => r.kind === 'value') as WithShow<DepreciatedValueRow>[]
  const valueByCode = new Map(values.map(v => [v.code, v]))

  const finalized = merged.map(r => {
    if (r.kind !== 'header') return r
    const prefix = r.code + '.'
    let sum = 0
    for (const [code, row] of valueByCode) {
      if (code === r.code || code.startsWith(prefix)) {
        if (!row.is_removed) {
          sum += row.depreciated_cost ?? 0
        }
      }
    }
    return {...r, depreciated_cost: sum, depreciated_per_unit: sum / size}
  })

  const _totalCost = finalized.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.total_cost
    },
    0
  )

  const totalDepreciatedCost = finalized.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.depreciated_cost
    },
    0
  )

  return {
    records: finalized,
    graphValues: [],
    totalCost: _totalCost,
    totalDepreciatedCost: totalDepreciatedCost,
    yearDiff: 0
  }
}

function mixedCalc(
  record: DepreciatedRecordRow, 
  {valuationDate, constructionDate}: {valuationDate: Date, constructionDate: Date},
  depreciation: DepreciatedRecordRow['depreciation']
) {
  if (depreciation.method === 2) {
    return sCurve(
      record.total_cost,
      valuationDate,
      constructionDate,
      depreciation.economicLife,
      depreciation.sCurveSteepness
    )
  }
  if (depreciation.method === 5) {
    return diminishingBalance(
      record.total_cost,
      valuationDate,
      constructionDate,
      depreciation.depreciationPerYear
    )
  }
  if (depreciation.method === 6) {
    return null
  }
  return null
}

type GroupedBucket = {
  key: string
  level: number
  depreciation?: DepreciatedRecordRow['depreciation']
  items: DepreciatedRecordRow[]
}

function toGroupKey(code: string, levelId: number): string {
  if (levelId <= 1) return ""
  const parts = code.split('.')
  if (levelId === 2) return parts[0]
  if (levelId === 3) return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0] ?? ''
  return code
}

function groupRecords(records: DepreciatedRecordRow[], levelId: number): GroupedBucket[] {
  const buckets = new Map<string, GroupedBucket>()
  for (const row of records) {
    if (row.kind === 'header') continue
    const key = toGroupKey(row.code, levelId)
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = {key, level: levelId, items: []}
      buckets.set(key, bucket)
    }
    bucket.items.push(row)
  }
  return Array.from(buckets.values())
}

function chooseGroupDepreciation(
  groupCode: string,
  allRows: DepreciatedRecordRow[]
): DepreciatedRecordRow['depreciation'] | undefined {
  if (!groupCode) return undefined
  const headerMatch = allRows.find(r => r.code === groupCode && r.kind === 'header' && r.depreciation);
  if (headerMatch) return headerMatch.depreciation

  const anyMatch = allRows.find(r => r.code === groupCode && r.depreciation)
  return anyMatch?.depreciation
}



function sCurve(
  value: number,
  valuationDate: Date,
  constructionDate: Date,
  economicLife: number,
  steepness: number
): number {
  const _valuationDate = moment(valuationDate)
  const _buildingConstructionDate = moment(constructionDate)
  const _steepness = steepness / 100
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    _valuationDate,
    _buildingConstructionDate
  )
  if (isNaN(yearDiff)) return null;

  const exponent = -_steepness * (yearDiff - (economicLife / 2))
  const sCurveFactor = 1 / (1 + Math.exp(exponent))
  const depreciatedValue = value * (1 - sCurveFactor)
  return depreciatedValue
}

function diminishingBalance(
  value: number,
  valuationDate: Date,
  constructionDate: Date,
  factor: number,
): number {
  const _valuationDate = moment(valuationDate)
  const _buildingConstructionDate = moment(constructionDate)
  const _factor = factor / 100
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    _valuationDate,
    _buildingConstructionDate
  )
  if (isNaN(yearDiff)) return null;
  const depreciatedValue = value * Math.pow((1 - _factor), yearDiff)
  return depreciatedValue
}

function isDepreciationComplete(depreciation: DepreciatedRecordRow['depreciation']): boolean {
  if (!depreciation) return false
  if (depreciation.method === 2) {
    return depreciation.economicLife !== null && depreciation.sCurveSteepness !== null
  }
  if (depreciation.method === 5) {
    return depreciation.depreciationPerYear !== null 
  }
  if (depreciation.method === 6) {
    return depreciation.economicLife !== null && depreciation.salvageValue !== null
  }
  return false
}